import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ChefHat,
  Sparkles,
  Utensils,
  ChevronRight,
  ChevronDown,
  Star,
  Minus,
  Plus,
  Award,
  ShieldCheck,
  Users,
  Leaf,
  Clock,
  Salad,
  Gift,
  Flame,
  MessageCircle,
} from "lucide-react";
import { getWhatsappNumbers, buildWhatsappLink } from "@/lib/whatsappLink";
import api, { imageUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/components/Seo";

const EVENT_TYPES = ["wedding", "reception", "corporate", "birthday", "housewarming", "other"];

const VALUE_PILLARS = [
  { icon: Award, label: "Premium Quality" },
  { icon: ShieldCheck, label: "Hygienic Kitchen" },
  { icon: Users, label: "Experienced Team" },
  { icon: Leaf, label: "Fresh Ingredients" },
];

const WHY_PILLARS = [
  { icon: Salad, label: "Vegetarian Focus" },
  { icon: ShieldCheck, label: "Hygienic & Safe" },
  { icon: Leaf, label: "Fresh Ingredients" },
  { icon: Users, label: "Experienced Team" },
  { icon: Clock, label: "On-time Service" },
];

const DiamondDivider = ({ className = "" }) => (
  <div className={`flex items-center justify-center gap-2 ${className}`} aria-hidden>
    <span className="h-px w-6 bg-accent" />
    <span className="h-1.5 w-1.5 rotate-45 bg-accent" />
    <span className="h-px w-6 bg-accent" />
  </div>
);


export default function Home() {
  useSeo({
    title: "Catering Services in Mumbai — Weddings, Corporate & Events | Tezz Khana",
    description: "Tezz Khana Catering Service in Mumbai — professional catering for weddings, receptions, corporate events, birthdays and housewarmings. 130+ dishes, flexible packages and custom quotes.",
    path: "/",
  });
  const { t, eventInfo, setEventInfo, settings } = useApp();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [popular, setPopular] = useState([]);
  const [packages, setPackages] = useState([]);
  const [showAllCats, setShowAllCats] = useState(false);
  const [type, setType] = useState(eventInfo.type || "");
  const [guests, setGuests] = useState(() => {
    const n = Number(eventInfo.guests);
    return Number.isFinite(n) && n >= 50 ? Math.floor(n) : 50;
  });

  useEffect(() => {
    (async () => {
      try {
        const [c, d, p] = await Promise.all([
          api.get("/categories"),
          api.get("/dishes", { params: { popular: true } }),
          api.get("/packages"),
        ]);
        setCategories(c.data || []);
        setPopular(d.data || []);
        setPackages(p.data || []);
      } catch (e) {
        console.warn("Home load", e?.message);
      }
    })();
  }, []);

  const catById = useMemo(() => {
    const m = {};
    categories.forEach((c) => (m[c.id] = c));
    return m;
  }, [categories]);

  const startOrder = () => {
    if (!type) return;
    const n = Number(guests);
    const clamped = Number.isFinite(n) && n >= 50 ? Math.floor(n) : 50;
    if (clamped !== guests) setGuests(clamped);
    setEventInfo({ type, guests: String(clamped) });
    navigate("/build");
  };

  const goBuild = () => {
    const n = Number(guests);
    const clamped = Number.isFinite(n) && n >= 50 ? Math.floor(n) : 50;
    if (clamped !== guests) setGuests(clamped);
    setEventInfo({ type, guests: String(clamped) });
    navigate("/build");
  };

  const clampGuestsOnBlur = () => {
    const n = Number(guests);
    if (!Number.isFinite(n) || n < 50) setGuests(50);
    else setGuests(Math.floor(n));
  };

  const renderSignatureCard = (mobile) => (
    <div className="rounded-3xl border border-border/70 bg-card p-6 sm:p-8" data-testid={mobile ? "home-signature-card-mobile" : "home-signature-card"}>
      <div className="flex items-center justify-between gap-2 sm:gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-accent">
            <Gift className="h-4 w-4" />
            <span className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.14em] sm:text-[10px] sm:tracking-[0.28em]">
              Signature Packages
            </span>
          </div>
          <div className="mt-2 flex items-center justify-between gap-1 sm:block">
            <div className="inline-block">
              <h3 className="whitespace-nowrap font-serif-display text-base font-semibold text-secondary sm:whitespace-normal sm:text-3xl">
                Signature packages
              </h3>
              <DiamondDivider className="mt-1.5" />
            </div>
            <Link
              to="/packages"
              data-testid="home-view-all-packages-m"
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-border/70 bg-background px-2.5 py-1 text-[10px] font-semibold text-primary hover:bg-muted sm:hidden"
            >
              View all packages <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>
        <Link
          to="/packages"
          data-testid={mobile ? "home-view-all-packages-d" : "home-view-all-packages"}
          className={`hidden shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-border/70 bg-background px-4 py-1.5 text-sm font-semibold text-primary hover:bg-muted sm:inline-flex`}
        >
          View all packages <ChevronRight className="h-4 w-4" />
        </Link>
      </div>

      {packages.length === 0 ? (
        <div
          data-testid={mobile ? "home-empty-packages-m" : "home-empty-packages"}
          className="mt-6 flex items-start gap-4 rounded-2xl border border-dashed border-border/70 bg-background p-5"
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-primary/5 text-primary">
            <Gift className="h-5 w-5" />
          </span>
          <div>
            <div className="text-sm font-medium text-secondary">
              No packages have been added yet.
            </div>
            <div className="mt-1 text-xs font-medium text-foreground/80">
              Create Silver, Gold, Premium or Custom packages in Admin to display them here.
            </div>
          </div>
        </div>
      ) : (
        <ul className="mt-6 grid grid-cols-2 gap-3 sm:block sm:space-y-3">
          {packages.slice(0, 4).map((p) => (
            <li
              key={p.id}
              className="rounded-2xl border border-border/70 bg-background p-3 sm:p-4"
              data-testid={`home-package-preview-${p.id}${mobile ? "-m" : ""}`}
            >
              <div className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.22em] text-accent">
                {p.tier}
              </div>
              <div className="mt-1 font-serif-display text-sm font-semibold leading-snug text-secondary sm:text-lg">
                {p.name}
              </div>
              {p.minimum_guests && (
                <div className="mt-1 whitespace-nowrap text-xs font-medium text-foreground/80">
                  Min. {p.minimum_guests} guests
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="relative overflow-hidden bg-secondary text-secondary-foreground grain"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle at 85% 30%, rgba(212,175,55,0.25) 0%, transparent 55%), radial-gradient(circle at 15% 80%, rgba(123,17,19,0.55) 0%, transparent 55%)",
          }}
        />
        <div
          aria-hidden
          className="absolute -right-24 top-10 hidden h-[520px] w-[520px] rounded-full opacity-15 blur-3xl lg:block"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.7) 0%, transparent 70%)" }}
        />

        <div className="container-x relative z-10 flex flex-col justify-center pt-5 pb-2 sm:pt-8 sm:pb-3">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center gap-3 text-accent">
              <span aria-hidden className="h-px w-10 bg-accent/60" />
              <span
                data-testid="hero-eyebrow"
                className="text-xs font-semibold uppercase tracking-[0.4em] sm:text-sm"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Taste the Tradition
              </span>
              <span aria-hidden className="h-px w-10 bg-accent/60" />
            </div>

            <h1
              data-testid="hero-title"
              className="mt-3 text-[44px] leading-[0.92] tracking-[0.02em] sm:mt-3 sm:text-6xl md:text-7xl lg:text-[80px]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 900 }}
            >
              <span className="text-secondary-foreground">TEZZ&nbsp;KHANA</span>
            </h1>
            <div className="mt-2 flex items-center justify-center gap-3 sm:mt-3">
              <span aria-hidden className="h-px w-12 bg-accent/70" />
              <span
                data-testid="hero-subtitle"
                className="text-xs font-semibold uppercase tracking-[0.3em] text-accent sm:text-base sm:tracking-[0.42em] md:text-lg"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Catering Service
              </span>
              <span aria-hidden className="h-px w-12 bg-accent/70" />
            </div>

            <h2 className="mt-3 max-w-xl text-sm font-medium text-secondary-foreground/95 sm:mt-3 sm:text-base">
              Catering service in Mumbai for every occasion — {t("hero.subtitle")}
            </h2>

            {/* Hero action buttons */}
            <div
              data-testid="hero-actions"
              className="mt-5 flex flex-wrap items-center justify-center gap-3 sm:mt-5"
            >
              <button
                data-testid="hero-action-explore-menu"
                onClick={() => navigate("/menu")}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-accent/60 bg-accent/10 px-5 text-sm font-semibold uppercase tracking-[0.14em] text-accent transition-colors hover:bg-accent hover:text-secondary"
              >
                <Utensils className="h-4 w-4" /> Explore Menu
              </button>
              <button
                data-testid="hero-action-request-quote"
                onClick={() => navigate("/quote")}
                className="hidden h-11 items-center justify-center gap-2 rounded-xl border border-white/30 px-5 text-sm font-semibold uppercase tracking-[0.14em] text-secondary-foreground hover:bg-white/10 sm:inline-flex"
              >
                Request Custom Quote <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {/* Value pillars — compact inline row */}
            <div
              data-testid="hero-value-pillars"
              className="mt-4 flex w-full max-w-3xl flex-wrap items-center justify-center gap-x-6 gap-y-1.5 sm:mt-4"
            >
              {VALUE_PILLARS.map((v) => {
                const Icon = v.icon;
                return (
                  <span
                    key={v.label}
                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-secondary-foreground/95 sm:text-[11px]"
                  >
                    <Icon className="h-3.5 w-3.5 text-accent" aria-hidden />
                    {v.label}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Event Selector Bar - visually connected to hero */}
        <div className="relative z-10 pb-8 sm:pb-12">
          <div className="container-x">
            <div className="mb-1.5 text-center">
              <span className="inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-accent">
                <Sparkles className="h-3 w-3" /> Plan your event
              </span>
            </div>
            <div
              data-testid="hero-event-selector"
              className="rounded-2xl border border-accent/40 bg-primary p-3.5 shadow-[0_25px_70px_-20px_rgba(0,0,0,0.6)] ring-1 ring-accent/20 sm:p-4"
            >
              <div className="grid gap-2.5 sm:gap-3 lg:grid-cols-[1.1fr_1fr_auto_auto_auto] lg:items-end">
                {/* Event type */}
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-primary-foreground/90">
                    What is the occasion?
                  </label>
                  <select
                    data-testid="event-type-select"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="mt-1.5 w-full rounded-xl border border-white/10 bg-white px-4 py-3 text-sm font-semibold text-secondary focus:border-accent focus:outline-none"
                  >
                    <option value="">Select event type</option>
                    {EVENT_TYPES.map((et) => (
                      <option key={et} value={et}>
                        {t(`event.types.${et}`)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Guests */}
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-primary-foreground/90">
                    Expected guests
                  </label>
                  <div className="mt-1.5 flex items-center overflow-hidden rounded-xl border border-white/10 bg-white">
                    <button
                      type="button"
                      data-testid="event-guests-decrement"
                      onClick={() => setGuests((g) => Math.max(50, Math.floor(Number(g) || 50) - 1))}
                      className="grid h-11 w-11 shrink-0 place-items-center text-primary hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed"
                      aria-label="Decrease guests"
                      disabled={Number(guests) <= 50}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <input
                      data-testid="event-guest-count"
                      type="number"
                      min="50"
                      step="1"
                      value={guests}
                      onChange={(e) => setGuests(e.target.value)}
                      onBlur={clampGuestsOnBlur}
                      className="w-full bg-transparent px-2 py-3 text-center text-sm font-semibold text-secondary focus:outline-none"
                    />
                    <button
                      type="button"
                      data-testid="event-guests-increment"
                      onClick={() => setGuests((g) => Math.max(50, Math.floor(Number(g) || 50)) + 1)}
                      className="grid h-11 w-11 shrink-0 place-items-center text-primary hover:bg-muted"
                      aria-label="Increase guests"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* CTAs */}
                <button
                  data-testid="hero-start-order"
                  onClick={startOrder}
                  disabled={!type}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-accent bg-accent px-5 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                >
                  Start Your Order <ArrowRight className="h-4 w-4" />
                </button>

                <button
                  data-testid="hero-build-menu"
                  onClick={goBuild}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/30 bg-transparent px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-white/10"
                >
                  <ChefHat className="h-4 w-4" /> Build Your Menu
                </button>

                <Link
                  to="/quote"
                  data-testid="hero-request-quote"
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-white/30 bg-transparent px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-white/10"
                >
                  Request Custom Quote
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="container-x py-20 sm:py-24" data-testid="home-categories-section">
        <div className="text-center">
          <div className="inline-flex items-center gap-3 text-accent">
            <span aria-hidden className="h-px w-8 bg-accent/60" />
            <span className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.14em] sm:text-[10px] sm:tracking-[0.28em]">
              <Star className="mr-2 inline h-3 w-3 text-accent" />
              Explore our Menu
            </span>
            <span aria-hidden className="h-px w-8 bg-accent/60" />
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 sm:block">
            <div className="inline-block">
              <h2 className="font-serif-display text-lg font-semibold text-secondary sm:text-4xl">
                Explore by Category
              </h2>
              <DiamondDivider className="mt-2" />
            </div>
            <Link
              to="/menu"
              data-testid="home-view-all-menu-m"
              className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-border/70 bg-background px-2.5 py-1 text-xs font-semibold text-primary hover:bg-muted sm:hidden"
            >
              View full menu <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="mt-4 hidden justify-end sm:flex">
          <Link
            to="/menu"
            data-testid="home-view-all-menu"
            className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-background px-4 py-1.5 text-sm font-semibold text-primary hover:bg-muted"
          >
            View full menu <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        {categories.length === 0 ? (
          <p className="mt-8 text-center text-sm font-medium text-foreground/80" data-testid="home-empty-categories">
            {t("menu.empty_all")}
          </p>
        ) : (
          <>
            <div className="mt-8 sm:hidden">
              <label className="block text-[10px] font-semibold uppercase tracking-[0.2em] text-foreground/75">
                Choose a category
              </label>
              <select
                data-testid="home-category-select"
                defaultValue=""
                onChange={(e) => {
                  if (e.target.value) navigate(`/menu?category=${e.target.value}`);
                }}
                className="mt-2 w-full rounded-xl border border-border bg-card px-4 py-3 text-sm font-semibold text-secondary focus:border-primary focus:outline-none"
              >
                <option value="" disabled>Select a category…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div
              className="mt-10 hidden grid-cols-2 gap-4 sm:grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 lg:gap-5"
              data-testid="home-category-grid"
            >
            {(showAllCats ? categories : categories.slice(0, 6)).map((c) => (
              <Link
                key={c.id}
                to={`/menu?category=${c.id}`}
                data-testid={`home-category-tile-${c.slug}`}
                className="group flex flex-col items-center rounded-2xl border border-border/70 bg-card px-4 py-6 text-center transition-transform duration-200 hover:-translate-y-1 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5"
              >
                <span className="grid h-14 w-14 place-items-center rounded-full border border-primary/15 bg-primary/5 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <Utensils className="h-6 w-6" aria-hidden />
                </span>
                <div className="mt-4 text-[13px] font-semibold leading-snug text-secondary sm:text-sm">
                  {c.name}
                </div>
                <span aria-hidden className="mt-2 h-0.5 w-6 rounded-full bg-accent/70 transition-all group-hover:w-12" />
              </Link>
            ))}
            </div>
            {!showAllCats && categories.length > 6 && (
              <div className="mt-6 hidden justify-center sm:flex">
                <button
                  type="button"
                  data-testid="home-view-more-categories"
                  onClick={() => setShowAllCats(true)}
                  className="btn-ghost"
                >
                  View More Categories <ChevronDown className="h-4 w-4" />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      {/* POPULAR + SIGNATURE PACKAGES - side by side */}
      <section className="border-y border-border/60 bg-muted/40 py-16 sm:py-20" data-testid="home-highlights-section">
        <div className={`container-x grid gap-6 ${popular.length > 0 ? "lg:grid-cols-2" : ""}`}>
          {/* Popular dishes card — hidden entirely (all screens) when no popular dishes */}
          {popular.length > 0 && (
          <div className="order-2 flex flex-col rounded-3xl border border-border/70 bg-card p-6 sm:p-8 lg:order-none" data-testid="home-popular-card">
            <div className="flex items-center gap-2 text-accent">
              <Flame className="h-4 w-4" />
              <span className="whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.14em] sm:text-[10px] sm:tracking-[0.28em]">Popular Dishes</span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div className="inline-block">
                <h3 className="whitespace-nowrap font-serif-display text-base font-semibold text-secondary sm:whitespace-normal sm:text-3xl">
                  What guests love most?
                </h3>
                <DiamondDivider className="mt-1.5" />
              </div>
              <Link
                to="/menu"
                data-testid="home-browse-menu-m"
                className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-primary hover:underline sm:hidden"
              >
                Browse full menu <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>

              <ul className="mt-6 grid grid-cols-2 gap-3 sm:block sm:divide-y sm:divide-border/60">
                {popular.slice(0, 4).map((d) => (
                  <li
                    key={d.id}
                    className="rounded-2xl border border-border/70 bg-background p-2.5 sm:flex sm:items-center sm:gap-4 sm:rounded-none sm:border-0 sm:bg-transparent sm:p-0 sm:py-3"
                    data-testid={`home-popular-item-${d.id}`}
                  >
                    {d.image_id ? (
                      <img
                        src={imageUrl(d.image_id)}
                        alt={d.name}
                        loading="lazy"
                        className="h-20 w-full rounded-lg border border-border/60 object-cover sm:h-14 sm:w-14 sm:shrink-0 sm:rounded-xl"
                        data-testid={`home-popular-img-${d.id}`}
                      />
                    ) : (
                      <div className="grid h-14 w-14 shrink-0 place-items-center rounded-xl border border-dashed border-primary/40 bg-muted text-[8px] font-semibold uppercase tracking-[0.14em] text-primary/70">
                        IMG
                      </div>
                    )}
                    <div className="mt-1.5 min-w-0 flex-1 sm:mt-0">
                      <div className="font-serif-display text-sm font-semibold leading-snug text-secondary sm:truncate sm:text-base">
                        {d.name}
                      </div>
                      <div className="truncate text-xs font-medium text-foreground/80">
                        {catById[d.category_id]?.name || ""}
                      </div>
                    </div>
                  </li>
                ))}
                <li className="hidden sm:block sm:pt-3">
                  <Link to="/menu" className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-semibold text-primary hover:underline sm:text-sm">
                    Browse full menu <ChevronRight className="h-4 w-4" />
                  </Link>
                </li>
              </ul>
          </div>
          )}

          {/* Signature packages card */}
          <div className="order-1 lg:order-none">
            {renderSignatureCard(false)}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE TEZZ KHANA - dark pillar band */}
      <section className="bg-secondary py-7 text-secondary-foreground sm:py-8" data-testid="home-why-section">
        <div className="container-x">
          <div className="text-center">
            <div className="inline-flex items-center gap-3 text-accent">
              <span aria-hidden className="h-px w-10 bg-accent/60" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.32em]" style={{ fontFamily: "'Playfair Display', serif" }}>
                The Tezz Khana promise
              </span>
              <span aria-hidden className="h-px w-10 bg-accent/60" />
            </div>
            <div className="mt-2">
              <div className="inline-block">
                <h2 className="font-serif-display text-2xl font-semibold sm:text-3xl">
                  Why choose Tezz Khana?
                </h2>
                <DiamondDivider className="mt-2" />
              </div>
            </div>
          </div>

          <p
            data-testid="home-why-compact-line"
            className="mt-4 text-center text-sm font-medium text-secondary-foreground sm:hidden"
          >
            Vegetarian Focus • Hygienic &amp; Safe • On-time Service
          </p>
          <div className="mt-5 hidden grid-cols-2 gap-3 sm:grid sm:grid-cols-3 lg:grid-cols-5" data-testid="home-why-pillars">
            {WHY_PILLARS.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.label} className="flex flex-col items-center text-center">
                  <span className="grid h-10 w-10 place-items-center rounded-full border border-accent/50 bg-accent/10 text-accent">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="mt-2 text-sm font-semibold text-secondary-foreground">
                    {p.label}
                  </div>
                  <div className="mt-1 h-px w-8 bg-accent/60" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA BAND */}
      <section className="bg-primary py-14 text-primary-foreground sm:py-16" data-testid="home-cta-band">
        <div className="container-x flex flex-col items-center justify-between gap-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <span className="hidden h-14 w-14 shrink-0 place-items-center rounded-full border border-accent/50 bg-accent/10 text-accent sm:grid">
              <Sparkles className="h-6 w-6" />
            </span>
            <div className="text-center sm:text-left">
              <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-accent">
                Every event is unique
              </div>
              <h3 className="mt-1 font-serif-display text-2xl font-semibold sm:text-3xl">
                Let&apos;s make your special day unforgettable.
              </h3>
            </div>
          </div>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Link
              to="/quote"
              data-testid="home-cta-quote"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-accent px-6 text-sm font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              Request Custom Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <span className="text-xs uppercase tracking-[0.24em] text-primary-foreground/85">or</span>
            <Link
              to="/build"
              data-testid="home-cta-build"
              className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-primary-foreground/40 px-6 text-sm font-semibold text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ChefHat className="h-4 w-4" /> Build Your Menu
            </Link>
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section data-testid="home-service-areas" className="container-x py-14">
        <div className="rounded-3xl border border-border/70 bg-card p-8 sm:p-10">
          <span className="kbd-eyebrow">Service Areas</span>
          <h2 className="mt-3 text-base font-semibold text-secondary md:text-lg">
            Areas we serve in Mumbai
          </h2>
          <p className="mt-2 max-w-2xl text-sm font-medium text-foreground/85">
            Based in Goregaon East, Tezz Khana provides{" "}
            <Link to="/vegetarian-catering-mumbai" data-testid="home-veg-catering-link" className="font-semibold text-primary hover:underline">
              vegetarian catering
            </Link>{" "}
            across Mumbai and nearby areas —
            from South Mumbai to the Western and Central suburbs, Navi Mumbai and Thane —
            for{" "}
            <Link to="/wedding-catering-mumbai" data-testid="home-wedding-catering-link" className="font-semibold text-primary hover:underline">
              weddings
            </Link>
            ,{" "}
            <Link to="/corporate-catering-mumbai" data-testid="home-corporate-catering-link" className="font-semibold text-primary hover:underline">
              corporate events
            </Link>
            ,{" "}
            <Link to="/birthday-family-catering-mumbai" data-testid="home-family-catering-link" className="font-semibold text-primary hover:underline">
              family celebrations
            </Link>{" "}
            and{" "}
            <Link to="/catering-for-large-events-mumbai" data-testid="home-large-events-link" className="font-semibold text-primary hover:underline">
              large gatherings
            </Link>
            .
          </p>
          <div className="mt-4 grid max-w-xl grid-cols-3 gap-2" data-testid="home-service-areas-chips">
            {["Goregaon", "Andheri", "Malad", "Borivali", "Bandra", "Juhu", "Powai", "Dadar", "South Mumbai", "Navi Mumbai", "Thane"].map((a) => (
              <span key={a} className="rounded-full border border-border/70 bg-background px-3 py-1.5 text-center text-xs font-medium text-secondary">
                {a}
              </span>
            ))}
            <a
              href={buildWhatsappLink(getWhatsappNumbers(settings)[0], "Hi Tezz Khana! Do you cater in my area?") || "/contact"}
              target="_blank"
              rel="noreferrer"
              data-testid="home-service-areas-ask-us"
              className="inline-flex items-center justify-center gap-1 rounded-full bg-accent px-3 py-1.5 text-center text-xs font-semibold text-secondary transition-transform hover:-translate-y-0.5"
            >
              <MessageCircle className="h-3.5 w-3.5" /> Ask Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
